import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Alert, Button, Flex, Modal, Tooltip, Typography } from 'antd';
import {
  LikeFilled,
  ExclamationCircleFilled,
  SmileOutlined,
} from '@ant-design/icons';
import {
  useApolloClient,
  useLazyQuery,
  useMutation,
} from '@apollo/client/react';
import dayjs from 'dayjs';

import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import SectionMessage from 'components/SectionMessage/SectionMessage';
import useModalControls from 'hooks/useModalControls';
import useTimeoutValue from 'hooks/useTimeoutValue';
import { PAYMENTS } from 'queries/payments';
import { UPDATE_SETTLEMENT_PAYMENT_PLAN } from 'mutations/settlements';
import { formatDateToSubmit } from 'utilities/datesAndTimes';
import { getEftaText, formatDollarsFromCents } from 'utilities/helpers';
import {
  SETTLEMENT_OFFER_STATES,
  SETTLEMENT_OFFER_TYPES,
  SIZE,
  STATUS,
} from 'utilities/constants';

import {
  deduplicateScheduledPayments,
  updatePaymentsWithPaymentState,
} from '../paymentUtils';
import EditPaymentModal from './EditPaymentModal';
import EFTAForm from './EFTAForm';
import RecoveriesScheduleTable from './RecoveriesScheduleTable';
import { paymentsExceedOrUnderBalanceAlert } from './settlementPaymentUtils';
import {
  RECOVERIES_STEPS,
  setNextStep,
  useRecoveriesState,
  setPaymentPlan,
  setPayments,
} from './recoveriesState';

const { Title } = Typography;
const { confirm } = Modal;

const withKeysSortedByDate = (schedule) =>
  schedule
    .map((payment, index) => ({ ...payment, key: `payment-${index}` }))
    .sort((a, b) => dayjs(a.date) - dayjs(b.date));

const ScheduledPaymentsTable = ({ thirdParty = {} }) => {
  const { accountId, customerId } = useParams();
  const client = useApolloClient();

  const { eftaDetails, fundingAccount, offer, paymentPlan, payments } =
    useRecoveriesState();

  const isOfferSetup = !offer?.state;
  const isPendingCloseOut =
    offer?.state === SETTLEMENT_OFFER_STATES.PENDING_CLOSE_OUT;

  const isFulFilled = offer?.state === SETTLEMENT_OFFER_STATES.FULFILLED;

  const isPifOffer = offer?.offerType === SETTLEMENT_OFFER_TYPES.PIF;

  const { name: thirdPartyName, email: thirdPartyEmail } = thirdParty;

  const editPaymentModal = useModalControls();
  const eftaModal = useModalControls();

  const [paymentPlanSchedule, setPaymentPlanSchedule] = useState([]);

  const [editPaymentFormInitialValues, setEditPaymentFormInitialValues] =
    useState({});

  const [updatePaymentType, setUpdatePaymentType] = useState('');

  const [submitNewPaymentPlanErrorMsg, setSubmitNewPaymentPlanErrorMsg] =
    useState('');
  const [submitNewPaymentPlanSuccessMsg, setSubmitNewPaymentPlanSuccessMsg] =
    useTimeoutValue('', 3000);

  const onAddPayment = () => {
    setUpdatePaymentType('add');
    editPaymentModal.show();
  };

  /* 
    There have been a few cases recently with a katabat migration and debt sale 
    where acceptanceExpiration is set to a future date, which fails the payments query.
    This logic uses the acceptedAt date as a fallback to ensure the query's fromDate is in the past.
  */
  // const settlementPaymentsDateRange = {
  //   fromDate:
  //     dayjs(offer.acceptanceExpiration).isAfter(dayjs()) && offer.acceptedAt
  //       ? formatDateToSubmit(dayjs(offer.acceptedAt).subtract(1, 'day'))
  //       : formatDateToSubmit(
  //           dayjs(offer.acceptanceExpiration).subtract(1, 'day'),
  //         ),
  //   toDate: formatDateToSubmit(dayjs().add(1, 'day')),
  // };

  const [getPayments, getPaymentsQuery] = useLazyQuery(PAYMENTS, {
    fetchPolicy: 'network-only',
  });

  const fetchPayments = async () => {
    try {
      /* 
        There have been a few cases recently with a katabat migration and debt sale 
        where acceptanceExpiration is set to a future date, which fails the payments query.
        This logic uses the acceptedAt date as a fallback to ensure the query's fromDate is in the past.
      */
      const settlementPaymentsDateRange = {
        fromDate:
          dayjs(offer.acceptanceExpiration).isAfter(dayjs()) && offer.acceptedAt
            ? formatDateToSubmit(dayjs(offer.acceptedAt).subtract(1, 'day'))
            : formatDateToSubmit(
                dayjs(offer.acceptanceExpiration).subtract(1, 'day'),
              ),
        toDate: formatDateToSubmit(dayjs().add(1, 'day')),
      };

      const response = getPayments({
        variables: {
          accountId,
          customerId,
          fromDate: settlementPaymentsDateRange.fromDate,
          toDate: settlementPaymentsDateRange.toDate,
        },
      });

      const account = response.data.account;

      const paymentsWithPaymentState = updatePaymentsWithPaymentState(
        payments,
        account.payments,
      );

      setPayments({
        payments: paymentsWithPaymentState,
      });
    } catch {
      // For review: error handling
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [customerId, accountId]);

  const [submitNewPaymentPlan, { loading: submitNewPaymentPlanLoading }] =
    useMutation(UPDATE_SETTLEMENT_PAYMENT_PLAN, {
      onCompleted: () => {
        setSubmitNewPaymentPlanSuccessMsg('Payment plan successfully updated!');
        setTimeout(eftaModal.hide, 2500);
        setPaymentPlan({
          ...paymentPlan,
          pendingDeleted: [],
          updatedFuture: [],
        });
      },
      onError: (error) => {
        let errorMessage = 'Failed to update payment plan. Please try again.';
        if (
          error.message.includes('must be between') &&
          error.message.includes('inclusively')
        ) {
          // Parse the error message and format numbers as dollars
          const formattedMessage = error.message.replace(/(\d+)/g, (match) =>
            formatDollarsFromCents(parseInt(match, 10)),
          );
          errorMessage = `Failed to update payment plan: ${formattedMessage}`;
        }

        setSubmitNewPaymentPlanErrorMsg(errorMessage);
      },
      refetchQueries: ['GetSettlementOffers'],
    });

  const [cancelPayments, { loading: cancelingPayments }] = useMutation(
    UPDATE_SETTLEMENT_PAYMENT_PLAN,
    {
      onCompleted: () => {
        setSubmitNewPaymentPlanSuccessMsg(
          'Payment Plan successfully canceled!',
        );
        setTimeout(() => {
          setSubmitNewPaymentPlanSuccessMsg('');
          // to prevent refetch before timeout finishes
          client.refetchQueries({ include: ['GetSettlementOffers'] });
        }, 2500);
      },
      onError: () => {
        setSubmitNewPaymentPlanErrorMsg(
          'Failed to delete payment plan. Please try again.',
        );
      },
    },
  );

  const onCancelRemainingPayments = async () => {
    const variables = {
      authorizationText: '',
      customerId,
      transactions: [],
      offerId: offer.offerId,
    };

    await cancelPayments({ variables });
  };

  const confirmCancelRemainingPayments = () => {
    confirm({
      title:
        'Are you sure you want to cancel all remaining scheduled payments?',
      icon: <ExclamationCircleFilled />,
      okText: 'Yes',
      onOk() {
        return onCancelRemainingPayments();
      },
      onCancel() {},
    });
  };

  const { customerName, email, cardLast4, fundingAccountLast4 } = eftaDetails;

  const onConfirmNewSchedule = async () => {
    const updatedPaymentSchedule = paymentPlan?.updatedFuture
      ?.filter((payment) => payment.status !== 'delete')
      ?.map((payment) => ({
        amount: payment.amount,
        date: payment.date,
      }));

    const eftaText = getEftaText({
      eftaDetails: {
        cardLast4,
        customerName: thirdPartyName ?? customerName,
        fundingAccountLast4,
        email: thirdPartyEmail ?? email,
      },
      todaysDate: dayjs().format('L'),
      paymentPlanSchedule: updatedPaymentSchedule,
    });

    const variables = {
      authorizationText: eftaText,
      customerId,
      transactions: updatedPaymentSchedule,
      offerId: offer.offerId,
    };

    await submitNewPaymentPlan({ variables });
  };

  const onEditPayment = (record) => {
    setEditPaymentFormInitialValues({
      amount: Number((record.amount / 100).toFixed(2)),
      date: dayjs(record.date),
    });
    setUpdatePaymentType('edit');
    editPaymentModal.show();
  };

  const paymentsBalanceAlert = useMemo(() => {
    return paymentsExceedOrUnderBalanceAlert({ offer, paymentPlan, payments });
  }, [
    offer?.balance,
    paymentPlan?.updatedFuture,
    paymentPlan?.current,
    payments,
  ]);

  useEffect(() => {
    if (!paymentPlan?.current?.length && !payments?.length) {
      return setPaymentPlanSchedule([]);
    }

    if (!isOfferSetup) {
      const hasUpdatedFuturePayments = paymentPlan?.updatedFuture?.length;

      const scheduledPayments = hasUpdatedFuturePayments
        ? paymentPlan?.updatedFuture
        : paymentPlan?.future;

      const dedupedScheduledPayments = deduplicateScheduledPayments(
        payments,
        scheduledPayments,
      );
      const tableData = payments
        ? payments?.concat(dedupedScheduledPayments)
        : dedupedScheduledPayments;

      setPaymentPlanSchedule(withKeysSortedByDate(tableData));
    } else {
      const { current } = paymentPlan;
      const tableData = current;

      setPaymentPlanSchedule(withKeysSortedByDate(tableData));
    }
  }, [paymentPlan, payments]);

  /*
    - Flexible payment plans will have no active payment plan and no past payment plan ids. 
    - Offers where the original funding account has been marked non-op will also have no active payment plan, BUT 
      will have past payment plan ids.
      - {paymentPlanCanceledLegacy} 
      Previously, the offer aggregate kept the non-op funding account that canceled the autopay schedule
      - {paymentPlanWasInvoluntarilyCanceled}
      Going forward from Oct 2025, the offer aggregate will drop the funding account 
    Agents need to add payment(s) > add funding account > to create a new payment plan for both cases.
  */

  const hasNoPaymentPlan = !offer?.isDebitCard && !offer?.hasActivePaymentPlan;

  const isFlexiblePlan =
    hasNoPaymentPlan && !fundingAccount.id && !offer?.hasPastPaymentPlanIds;

  const paymentPlanCanceledLegacy =
    fundingAccount.id && hasNoPaymentPlan && offer?.hasPastPaymentPlanIds;

  const paymentPlanWasInvoluntarilyCanceled =
    !fundingAccount.id && hasNoPaymentPlan && offer?.hasPastPaymentPlanIds;

  const agentAddedPayments =
    hasNoPaymentPlan && paymentPlan?.current?.length > 0;

  return (
    <Flex vertical={true}>
      {isPendingCloseOut && (
        <Alert
          banner
          showIcon
          icon={<LikeFilled />}
          type="info"
          title={
            <>
              <strong>
                The account is PENDING COMPLETION of their settlement offer.
              </strong>{' '}
              {paymentPlan?.future?.length > 0
                ? 'Customers may cancel their remaining scheduled payments.'
                : 'There are no remaining scheduled payments.'}
            </>
          }
          style={{ marginBottom: 'var(--spacing-sm)' }}
          data-testid="offer-pending-close-out-alert"
        />
      )}
      {isFulFilled && (
        <Alert
          banner
          showIcon
          icon={<SmileOutlined />}
          type="success"
          title={
            <>
              <strong>
                The account has been{' '}
                {isPifOffer ? 'PAID IN FULL' : 'SETTLED IN FULL'}.
              </strong>{' '}
              {paymentPlan?.future?.length > 0
                ? 'Customers may cancel their remaining scheduled payments.'
                : 'There are no remaining scheduled payments.'}
            </>
          }
          style={{ marginBottom: 'var(--spacing-sm)' }}
          data-testid="offer-fulfilled-alert"
        />
      )}
      <Title level={5}>Payments</Title>
      <QueryBoundary
        query={getPaymentsQuery}
        mode={QueryBoundary.MODE.MESSAGE}
        loadingMessage="Loading Payments"
      >
        <RecoveriesScheduleTable
          data={paymentPlanSchedule}
          onEditPayment={onEditPayment}
          showEditPayment={!isPendingCloseOut && !isFulFilled}
          showStatus={!isOfferSetup}
        />
      </QueryBoundary>

      {!agentAddedPayments &&
        (paymentPlanCanceledLegacy || paymentPlanWasInvoluntarilyCanceled) && (
          <Alert
            banner
            showIcon
            type="error"
            title="This offer's autopay schedule was canceled. Please add payment(s) and a new funding account to create a new autopay schedule."
            style={{ marginBottom: 'var(--spacing-sm)' }}
          />
        )}

      {!agentAddedPayments && isFlexiblePlan && (
        <Alert
          banner
          showIcon
          type="error"
          title="Please add payment(s) and a funding account to create a payment plan."
          style={{ marginBottom: 'var(--spacing-sm)' }}
        />
      )}
      {offer?.state === SETTLEMENT_OFFER_STATES.IN_PROGRESS &&
        agentAddedPayments && (
          <Alert
            banner
            showIcon
            type="error"
            title="Add a funding account to create a payment plan."
            style={{ marginBottom: 'var(--spacing-sm)' }}
          />
        )}
      {!isPendingCloseOut && !isFulFilled && (
        <Flex gap="var(--spacing-sm)" justify="right">
          <Button onClick={onAddPayment}>Add a Payment</Button>
          <Tooltip
            title={
              !fundingAccount?.id
                ? 'Add a funding account to create a payment plan.'
                : ''
            }
          >
            <Button
              type="primary"
              disabled={
                (!isOfferSetup && !paymentPlan?.updatedFuture?.length) ||
                paymentsBalanceAlert?.type === 'error' ||
                !fundingAccount?.id
              }
              onClick={
                isOfferSetup
                  ? () => setNextStep(RECOVERIES_STEPS.DISCLOSURE)
                  : eftaModal.show
              }
            >
              {isOfferSetup ? 'Continue' : 'Confirm New Schedule'}
            </Button>
          </Tooltip>
        </Flex>
      )}
      {(isPendingCloseOut || isFulFilled) &&
        paymentPlan?.future?.length > 0 && (
          <Button
            danger
            onClick={confirmCancelRemainingPayments}
            loading={cancelingPayments}
            style={{ marginTop: 'var(--spacing-lg)' }}
            data-testid="cancel-remaining-payments"
          >
            Cancel Remaining Scheduled Payments
          </Button>
        )}
      {!isPendingCloseOut && !isFulFilled && !paymentsBalanceAlert && (
        <Alert
          banner
          showIcon
          type="info"
          title="Customers can always edit their scheduled payments as long as they stay within the plan’s due date
             and as along as the amounts total to the remaining balance."
          style={{ marginTop: 'var(--spacing-sm)' }}
        />
      )}
      {!isPendingCloseOut && !isFulFilled && paymentsBalanceAlert && (
        <Alert
          banner
          type={paymentsBalanceAlert.type}
          title={paymentsBalanceAlert.message}
          style={{ marginTop: 'var(--spacing-sm)' }}
        />
      )}
      {submitNewPaymentPlanSuccessMsg && (
        <SectionMessage
          cover={true}
          status={STATUS.SUCCESS}
          size={SIZE.MD}
          text={submitNewPaymentPlanSuccessMsg}
          visible={submitNewPaymentPlanSuccessMsg}
        />
      )}
      <EditPaymentModal
        {...editPaymentModal}
        transactions={paymentPlanSchedule}
        initialValues={editPaymentFormInitialValues}
        setInitialValues={setEditPaymentFormInitialValues}
        type={updatePaymentType}
      />
      <Modal
        {...eftaModal}
        width={600}
        onCancel={() => {
          setSubmitNewPaymentPlanErrorMsg('');
          setSubmitNewPaymentPlanSuccessMsg('');
          eftaModal.hide();
        }}
        footer={[]}
        destroyOnHidden={true}
      >
        <EFTAForm
          eftaDetails={{
            cardLast4,
            customerName: thirdPartyName ?? customerName,
            email: thirdPartyEmail ?? email,
            fundingAccountLast4,
          }}
          onFinish={onConfirmNewSchedule}
          mutation={{
            loading: submitNewPaymentPlanLoading,
            error: submitNewPaymentPlanErrorMsg,
            success: submitNewPaymentPlanSuccessMsg,
          }}
        />
      </Modal>
    </Flex>
  );
};

export default ScheduledPaymentsTable;
